import React from 'react';
import {
  Avatar,
  Button,
  Flex,
  Image,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  FileLock,
  Lock,
  SignOut,
  User,
  UserSwitch,
  ChartBar,
  ChatsCircle,
  Kanban,
  Users,
  WifiHigh,
  Lightning,
  ClockCounterClockwise,
  Bell,
  FileText,
} from 'phosphor-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { useAuth } from '../../providers/auth';
import { useNotificationContext } from '../../providers/notification';
import { useNotificationStore } from '../../stores/whatsapp/notificationStore';
import logo from '../../assets/brand/logo-horizontal-color.svg';
import { ResponsiveMenu } from '../sidebar/ResponsiveMenu';
import { HeaderMenuDropdown } from './HeaderMenuDropdown';
import { MODULOS_SIDEBAR, isModuloAtivo } from '../sidebar/modulos';
import { useModuloDrill } from '../sidebar/useModuloDrill';

const HeaderNotificationBell = () => {
  const { unreadCount, openPanel } = useNotificationContext();
  // O painel tem duas fontes: notificacoes gerais (provider) e as do chat
  // WhatsApp (notificationStore). O badge do sino precisa somar as duas —
  // antes ignorava o WhatsApp e novas mensagens nao apareciam no contador.
  const waUnread = useNotificationStore(s => s.unreadCount);
  const totalUnread = unreadCount + waUnread;

  return (
    <Button
      variant="unstyled"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="36px"
      h="36px"
      minW="36px"
      borderRadius="full"
      color="#475569"
      transition="background 0.18s ease, color 0.18s ease"
      _hover={{ bg: '#F1F5F9', color: '#0F172A' }}
      aria-label="Notificações"
      onClick={openPanel}
    >
      <Bell size={20} weight="bold" />
      {totalUnread > 0 && (
        <Flex
          position="absolute"
          top="-2px"
          right="-2px"
          bg="red.500"
          color="white"
          borderRadius="full"
          minW="18px"
          h="18px"
          fontSize="10px"
          fontWeight="bold"
          alignItems="center"
          justifyContent="center"
          border="2px solid"
          borderColor="white"
          px="4px"
        >
          {totalUnread > 99 ? '99+' : totalUnread}
        </Flex>
      )}
    </Button>
  );
};

const RouteItem = ({ item, isActive }: any) => (
  <Link
    to={item.path}
    aria-current={isActive ? 'page' : undefined}
    className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-semibold tracking-wide transition-all ${
      isActive
        ? 'bg-white text-[#1B88F5] shadow-[0_2px_10px_-2px_rgba(27,136,245,0.35)] ring-1 ring-[#1B88F5]/25'
        : 'text-slate-600 hover:bg-white/80 hover:text-slate-900'
    }`}
  >
    {item.icon}
    {item.name}
  </Link>
);

// Abas do módulo no header: pílulas dentro de um trilho claro (padrão glass)
const TRILHO_ABAS = 'gap-1 rounded-full border border-slate-200/70 bg-slate-100/70 p-1';

/**
 * Módulos do sistema no cabeçalho quando a tela está num módulo em drill
 * (Integração, Fiscal, Contábil...): o menu lateral mostra os submódulos, e
 * daqui o usuário abre outro módulo numa NOVA guia sem sair do atual (24/10).
 * Só o ícone (11 módulos não cabem com nome ao lado do logo); o nome fica no
 * tooltip.
 */
const ModulosDoSistema = () => {
  const { pathname } = useLocation();
  const { hasPermission, isAdmin } = useAuth();
  const modulos = MODULOS_SIDEBAR.filter(m => isAdmin() || hasPermission(m.path));
  if (!modulos.length) return null;
  return (
    <Flex
      className="no-scrollbar hidden overflow-x-auto md:flex"
      align="center"
      flex={1}
      justify="center"
      px={2}
      gap={2}
    >
      <Text
        className="hidden xl:block"
        fontSize="10px"
        fontWeight={700}
        letterSpacing="0.12em"
        textTransform="uppercase"
        color="#94A3B8"
        whiteSpace="nowrap"
      >
        Módulos do sistema
      </Text>
      <Flex className={TRILHO_ABAS} flexShrink={0}>
        {modulos.map(m => {
          const ativo = isModuloAtivo(m.path, pathname);
          return (
            <Tooltip key={m.path} label={`${m.name} (abre em nova guia)`} hasArrow>
              <Link
                to={m.path}
                target="_blank"
                rel="noopener"
                aria-label={`Abrir ${m.name} em nova guia`}
                aria-current={ativo ? 'page' : undefined}
                className={`flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-1.5 py-1 text-[11px] font-semibold transition-all ${
                  ativo
                    ? 'bg-white text-[#1B88F5] shadow-[0_2px_10px_-2px_rgba(27,136,245,0.35)] ring-1 ring-[#1B88F5]/25'
                    : 'text-slate-600 hover:bg-white/80 hover:text-slate-900'
                }`}
              >
                <span
                  className="flex size-7 items-center justify-center rounded-full"
                  style={{ background: m.bgColor }}
                >
                  {m.icon}
                </span>
              </Link>
            </Tooltip>
          );
        })}
      </Flex>
    </Flex>
  );
};

const Header = () => {
  const { Logout, user, acessoDelegado, hasPermission, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isDrillMode } = useModuloDrill();

  // Função para filtrar rotas por permissão
  const filterRoutesByPermission = (routes: any[]) => {
    if (isAdmin()) return routes;
    return routes.filter(route => hasPermission(route.path));
  };

  // Fiscal e Integração saíram daqui (24/10): os submódulos ficam no menu
  // lateral, no padrão do Contábil (ver components/sidebar/submodulos.tsx).
  const isSubRoutePersonnelDepartment =
    location.pathname.startsWith('/modulesHome/personnelDepartment') &&
    location.pathname !== '/modulesHome/personnelDepartment';

  const isSubRouteWhatsApp = location.pathname.startsWith('/whatsapp');

  const renderRoutes = (routes: any[], visibleItems = routes.length) => {
    // Filtra rotas por permissão primeiro
    const filteredRoutes = filterRoutesByPermission(routes);

    const visible = filteredRoutes.slice(0, visibleItems);
    const hidden = filteredRoutes.slice(visibleItems);

    return (
      <>
        {visible.map(item => (
          <RouteItem key={item.path} item={item} isActive={item.path === location.pathname} />
        ))}
        {hidden.length > 0 && (
          <HeaderMenuDropdown
            items={hidden}
            currentPath={location.pathname}
            onNavigate={path => navigate(path)}
          />
        )}
      </>
    );
  };

  const OpenRoutesPersonnelDepartment = [
    {
      name: 'Acompanhamento folha de pagamento',
      path: '/modulesHome/personnelDepartment/payRoll',
      icon: <IoDocumentTextOutline color="#219653" className="size-5" />,
      bgColor: '#ABEDC6',
    },
  ];

  const OpenRoutesWhatsApp = [
    {
      name: 'CHAT',
      path: '/whatsapp',
      icon: <ChatsCircle color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'DASHBOARD',
      path: '/whatsapp/dashboard',
      icon: <ChartBar color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'KANBAN',
      path: '/whatsapp/kanban',
      icon: <Kanban color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'CONTATOS',
      path: '/whatsapp/clientes',
      icon: <Users color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'ANÁLISE INTELIGENTE',
      path: '/whatsapp/relatorios',
      icon: <ChartBar color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'HISTÓRICO',
      path: '/whatsapp/historico',
      icon: <ClockCounterClockwise color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'INSIGHTS',
      path: '/whatsapp/ai-insights',
      icon: <Lightning color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'TEMPLATES',
      path: '/whatsapp/templates',
      icon: <FileText color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
    {
      name: 'CONEXÃO',
      path: '/whatsapp/conexao',
      icon: <WifiHigh color="#25D366" className="size-4" />,
      bgColor: '#d4f5e0',
    },
  ];

  // Determinar quantos itens mostrar em cada breakpoint
  const getVisibleItemsCount = () => {
    if (isSubRoutePersonnelDepartment) return { md: 1, lg: 1, xl: 1 }; // Personnel (1 item)
    if (isSubRouteWhatsApp) return { md: 4, lg: 6, xl: 8 }; // WhatsApp (8 itens)
    return { md: 0, lg: 0, xl: 0 };
  };

  const visibleCounts = getVisibleItemsCount();

  return (
    <div
      className="fixed z-50 flex w-full items-center justify-between border-b border-slate-900/[0.06] px-3 py-2 pr-6"
      style={{
        background: 'rgba(255, 255, 255, 0.94)',
        boxShadow: '0 8px 28px -18px rgba(27, 136, 245, 0.45)',
      }}
    >
      <div className="flex flex-col md:w-full md:flex-row md:items-center">
        <div className="flex items-center gap-2 lg:pr-10">
          <div className="block md:hidden">
            <ResponsiveMenu />
          </div>
          <Link to="/" aria-label="Início">
            <Image
              src={logo}
              alt="Especialistas Inteligência Contábil"
              h={{ base: '30px', md: '36px' }}
              w="auto"
              draggable={false}
            />
          </Link>
        </div>

        {/* Menu WhatsApp */}
        {isSubRouteWhatsApp && (
          <Flex
            className="no-scrollbar hidden gap-1 overflow-x-auto md:flex"
            direction="row"
            align="center"
            flex={1}
            justify="center"
            px={2}
          >
            <Flex
              className={`hidden md:flex lg:hidden ${TRILHO_ABAS}`}
              display={{ md: 'flex', lg: 'none' }}
            >
              {renderRoutes(OpenRoutesWhatsApp, visibleCounts.md)}
            </Flex>
            <Flex
              className={`hidden lg:flex xl:hidden ${TRILHO_ABAS}`}
              display={{ lg: 'flex', xl: 'none' }}
            >
              {renderRoutes(OpenRoutesWhatsApp, visibleCounts.lg)}
            </Flex>
            <Flex
              className={`hidden xl:flex ${TRILHO_ABAS}`}
              display={{ base: 'none', xl: 'flex' }}
            >
              {renderRoutes(OpenRoutesWhatsApp, visibleCounts.xl)}
            </Flex>
          </Flex>
        )}

        {/* Módulo em drill (submódulos no menu lateral): módulos do sistema */}
        {isDrillMode && <ModulosDoSistema />}

        {/* Menu Personnel Department */}
        {isSubRoutePersonnelDepartment && (
          <Flex
            className="hidden gap-2 md:ml-4 md:flex lg:ml-8 xl:ml-12 2xl:ml-16"
            direction="row"
            align="center"
          >
            <Flex className={`hidden md:flex ${TRILHO_ABAS}`} display={{ md: 'flex' }}>
              {renderRoutes(OpenRoutesPersonnelDepartment, 1)}
            </Flex>
          </Flex>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center gap-3">
        {/* Sino de notificações */}
        <HeaderNotificationBell />

        {/* Badge de Acesso Delegado */}
        {acessoDelegado && (
          <Flex
            bg="purple.500"
            color="white"
            px={3}
            py={1}
            borderRadius="full"
            alignItems="center"
            gap={2}
            fontSize="xs"
            fontWeight="bold"
          >
            <UserSwitch size={16} weight="bold" />
            <Text>Acesso: {user?.nome}</Text>
          </Flex>
        )}

        <Popover>
          <PopoverTrigger>
            <Button variant="unstyled" aria-label="Minha conta">
              <Avatar
                size="sm"
                name={user?.nome}
                src={user?.imagem_url}
                boxShadow="0 0 0 2px #FFFFFF, 0 0 0 3.5px rgba(27,136,245,0.35)"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent mr="12px" p="5px" boxShadow="0px 4px 6px rgba(0, 0, 0, 0.09)">
            <PopoverCloseButton />
            <PopoverBody>
              <div className="flex flex-col items-start">
                <Link to="/perfil">
                  <Button variant="unstyled" p="6px" display="flex" alignItems="center" gap={2}>
                    <User size={20} weight="bold" />
                    <Text>Minha Conta</Text>
                  </Button>
                </Link>
                <Link to="/seguranca">
                  <Button variant="unstyled" p="6px" display="flex" alignItems="center" gap={2}>
                    <Lock size={20} weight="bold" />
                    <Text>Segurança</Text>
                  </Button>
                </Link>
                <Link to="/sessoes">
                  <Button variant="unstyled" p="6px" display="flex" alignItems="center" gap={2}>
                    <FileLock size={20} weight="bold" />
                    <Text>Sessões</Text>
                  </Button>
                </Link>
                <PopoverHeader w="full" />
                <Button
                  variant="unstyled"
                  p="6px"
                  display="flex"
                  alignItems="center"
                  gap={2}
                  onClick={Logout}
                >
                  <SignOut size={20} weight="bold" />
                  <Text>Sair</Text>
                </Button>
              </div>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

export default Header;
